import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  School as SchoolIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  SwapHoriz as SwapHorizIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';

import { Container, LoadingState, ErrorState, EmptyState, InfoLabel, InfoValue, SectionHeader, StyledCard, DataGrid } from '@/components/common';
import { useCourseDetail, useDeleteCourse, useRestoreCourse, useUpdateCourseStatus } from '@/hooks/courses.hook';
import { useVideoList, useDeleteVideo } from '@/hooks/videos.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { formatDate } from '@/util/dateUtils';
import CourseCategoryCell from '../../components/CourseCategoryCell';
import CourseLanguageCell from '../../components/CourseLanguageCell';
import VideoAddModal from './components/VideoAddModal';
import VideoEditModal from './components/VideoEditModal';
import CourseEditModal from './components/CourseEditModal';

/**
 * 과정 상세 페이지 컴포넌트
 * 
 * 과정의 상세 정보와 동영상 목록을 조회하고 관리하는 페이지입니다.
 */
const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  // 모달 상태 관리
  const [videoAddModalOpen, setVideoAddModalOpen] = useState(false);
  const [videoEditModalOpen, setVideoEditModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [courseEditModalOpen, setCourseEditModalOpen] = useState(false);

  // 과정 상세 정보 조회
  const { data: courseData, isLoading: courseLoading, error: courseError, refetch: refetchCourse } = useCourseDetail(courseId);

  // 동영상 목록 조회
  const { data: videoData, isLoading: videoLoading, refetch: refetchVideoList } = useVideoList({ courseId });
  // 백엔드 응답: ApiResponse<List<VideoResponse>> → response.data.data가 List
  const videoList = videoData?.data || videoData || [];

  // 동영상 삭제 훅
  const deleteVideoMutation = useDeleteVideo();
  
  // 과정 폐강/복구 훅
  const deleteCourseMutation = useDeleteCourse();
  const restoreCourseMutation = useRestoreCourse();
  const updateCourseStatusMutation = useUpdateCourseStatus();

  const isClosed = Boolean(courseData?.deletedAt);

  const handleToggleHasContent = useCallback(() => {
    if (!courseData) return;
    const currentOn = courseData.hasContent === true || courseData.hasContent === 1;
    const willRegister = !currentOn;

    const confirmText = willRegister
      ? '이 과정 콘텐츠를 학습자에게 공개(등록)하시겠습니까?'
      : '이 과정 콘텐츠를 비공개(미등록)로 전환하시겠습니까? 공개가 해제될 수 있습니다.';

    confirmToast(confirmText, async () => {
      try {
        await updateCourseStatusMutation.mutateAsync({
          courseId: Number(courseId),
          hasContent: currentOn ? 0 : 1,
        });
        successToast('콘텐츠 등록 상태가 변경되었습니다.');
      } catch (err) {
        const raw =
          err?.response?.data?.msg ??
          err?.response?.data?.message ??
          (typeof err?.response?.data === 'string' ? err.response.data : null) ??
          err?.message ??
          String(err);
        alert(raw);
      }
    });
  }, [courseData, courseId, updateCourseStatusMutation]);

  /**
   * 과정 폐강 실행 (force=true면 진행 중 그룹 경고를 무시하고 강행)
   */
  const closeCourse = useCallback(async (force) => {
    try {
      await deleteCourseMutation.mutateAsync({ courseId, force });
      successToast('과정이 폐강되었습니다.');
      navigate('/education/courses');
    } catch (error) {
      // 진행 중인 그룹에 편성되어 있어 거부된 경우(409): 확인 후 force로 재요청
      if (error.response?.status === 409) {
        const affectedGroups = error.response?.data?.data ?? [];
        const groupNames = affectedGroups.map((g) => g.groupName).filter(Boolean).join(', ');
        const detail = groupNames ? `\n대상 그룹: ${groupNames}` : '';
        confirmToast(
          `${error.response?.data?.msg ?? '진행 중인 그룹에 편성된 과정입니다.'}${detail}\n\n그래도 폐강하시겠습니까?`,
          () => closeCourse(true)
        );
        return;
      }
      errorToast(`과정 폐강에 실패했습니다. - ${error.response?.data?.msg ?? error.message}`);
    }
  }, [deleteCourseMutation, courseId, navigate]);

  const handleDeleteCourse = useCallback(() => {
    confirmToast(
      `"${courseData.courseName}" 과정을 폐강하시겠습니까? 영상과 수강 기록은 보존되며 나중에 복구할 수 있습니다.`,
      () => closeCourse(false)
    );
  }, [closeCourse, courseData]);

  /**
   * 과정 폐강 취소(복구) 핸들러
   */
  const handleRestoreCourse = useCallback(() => {
    confirmToast(
      `"${courseData.courseName}" 과정의 폐강을 취소하고 다시 운영 상태로 되돌리시겠습니까?`,
      async () => {
        try {
          await restoreCourseMutation.mutateAsync({ courseId });
          successToast('과정 폐강이 취소되었습니다.');
          refetchCourse();
        } catch (error) {
          errorToast(`폐강 취소에 실패했습니다. - ${error.response?.data?.msg ?? error.message}`);
        }
      }
    );
  }, [restoreCourseMutation, courseData, courseId, refetchCourse]);

  /**
   * 동영상 삭제 핸들러
   */
  const handleDeleteVideo = useCallback((video) => {
    confirmToast(
      `"${video.videoName}" 동영상을 삭제하시겠습니까?`,
      async () => {
        try {
          await deleteVideoMutation.mutateAsync({
            videoId: video.videoId,
            midibusCategoryId: courseData?.midibusCategoryId, // 과정에 연결된 미디버스 카테고리 ID
            midibusMediaId: video.mediaId, // 동영상의 미디버스 미디어 ID
          });
          successToast('동영상이 삭제되었습니다.');
        } catch (error) {
          errorToast(`동영상 삭제에 실패했습니다. - ${error.response?.data?.msg}`);
        }
      }
    );
  }, [deleteVideoMutation, courseData]);

  // 동영상 그리드 컬럼 정의
  const videoColumns = useMemo(() => [
    {
      field: 'videoSeq',
      headerName: '순서',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'videoName',
      headerName: '동영상명',
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'videoIntro',
      headerName: '소개',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.row.videoIntro || '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 150,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="수정">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVideo(params.row);
                setVideoEditModalOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteVideo(params.row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [courseId, handleDeleteVideo]);

  // 로딩 상태
  if (courseLoading) {
    return <LoadingState title="과정 정보 로딩 중" />;
  }

  // 에러 상태
  if (courseError || !courseData) {
    return (
      <ErrorState
        title="과정 정보 오류"
        message="과정 정보를 불러오는 중 오류가 발생했습니다."
        onGoBack={() => navigate('/education/courses')}
      />
    );
  }

  const isContentPublic = courseData.hasContent === true || courseData.hasContent === 1;

  return (
    <Container title="과정 상세">
      {/* 상단 네비게이션 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/education/courses')}
          variant="outlined"
        >
          목록으로
        </Button>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => setCourseEditModalOpen(true)}
            variant="outlined"
          >
            과정 수정
          </Button>
          {isClosed ? (
            <Button
              startIcon={<RestoreIcon />}
              onClick={handleRestoreCourse}
              variant="outlined"
              color="primary"
              disabled={restoreCourseMutation.isPending}
            >
              폐강 취소
            </Button>
          ) : (
            <Button
              startIcon={<DeleteIcon />}
              onClick={handleDeleteCourse}
              variant="outlined"
              color="error"
              disabled={deleteCourseMutation.isPending}
            >
              과정 폐강
            </Button>
          )}
        </Stack>
      </Stack>

      {isClosed && (
        <Alert severity="warning" icon={<DeleteIcon fontSize="inherit" />} sx={{ mb: 3 }}>
          이 과정은 폐강되었습니다
          {courseData.deletedAt && ` · ${formatDate(courseData.deletedAt)}`}
          {courseData.deletedBy && ` · ${courseData.deletedBy}`}
          . 학습자 목록과 신규 그룹 편성에서 제외되며, 영상·수강 기록은 보존됩니다. 상단의 "폐강 취소"로 되돌릴 수 있습니다.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 상단: 과정 기본 정보 */}
        <Grid item xs={6}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader>
                <SchoolIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h5" fontWeight={600}>
                  과정 정보
                </Typography>
              </SectionHeader>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>과정 ID</InfoLabel>
                  <InfoValue>{courseData.courseId}</InfoValue>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>과정명</InfoLabel>
                  <InfoValue>{courseData.courseName}</InfoValue>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>카테고리</InfoLabel>
                  <Box mt={1}>
                    <CourseCategoryCell row={{ courseType: courseData.category }} />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>언어</InfoLabel>
                  <Box mt={1}>
                    <CourseLanguageCell row={{ languageId: courseData.language?.id }} />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>과정 수</InfoLabel>
                  <InfoValue>{courseData.courseCount || '-'}</InfoValue>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>강사명</InfoLabel>
                  <InfoValue>{courseData.instructor?.instName || courseData.instructor?.instId || '-'}</InfoValue>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>무료 여부</InfoLabel>
                  <Box mt={1}>
                    <Chip
                      label={courseData.freeYn ? '무료' : '유료'}
                      size="small"
                      color={courseData.freeYn ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <InfoLabel>등록일</InfoLabel>
                  <InfoValue>{formatDate(courseData.createdAt) || '-'}</InfoValue>
                </Grid>

                <Grid item>
                  <InfoLabel>콘텐츠 공개 여부</InfoLabel>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip
                      label={isContentPublic ? '공개됨' : '미공개'}
                      size="small"
                      color={isContentPublic ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    <Tooltip title="클릭하면 확인 후 서버에 반영됩니다.">
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<SwapHorizIcon />}
                          onClick={handleToggleHasContent}
                          disabled={updateCourseStatusMutation.isPending}
                          aria-label={isContentPublic ? '비공개로 전환' : '공개하기'}
                        >
                          {isContentPublic ? '비공개로 전환' : '공개하기'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Grid>
                
                {courseData.courseObj && (
                  <Grid item xs={12}>
                    <InfoLabel>과정 목표</InfoLabel>
                    <InfoValue sx={{ whiteSpace: 'pre-wrap' }}>
                      {courseData.courseObj}
                    </InfoValue>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* 이미지 및 파일 섹션 */}
        <Grid item xs={6}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader>
                <ImageIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h5" fontWeight={600}>
                  이미지 및 파일
                </Typography>
              </SectionHeader>

              <Grid container spacing={3}>
                {/* 썸네일 이미지 */}
                {courseData.thumbnailImg && (
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLabel>썸네일 이미지</InfoLabel>
                    <Box mt={1} sx={{ width: '100%' }}>
                      <Box
                        component="img"
                        src={`${import.meta.env.VITE_API_KEY}/course-thumbnail-img/${courseData.thumbnailImg}`}
                        alt="썸네일 이미지"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Grid>
                )}

                {/* 소개 이미지 */}
                {courseData.introImg && (
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLabel>소개 이미지</InfoLabel>
                    <Box mt={1} sx={{ width: '100%' }}>
                      <Box
                        component="img"
                        src={`${import.meta.env.VITE_API_KEY}/course-intro-img/${courseData.introImg}`}
                        alt="소개 이미지"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Grid>
                )}

                {/* 강사 이미지 */}
                {courseData.instructor?.instImage && (
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoLabel>강사 이미지</InfoLabel>
                    <Box mt={1} sx={{ textAlign: 'center' }}>
                      <img
                        src={`${import.meta.env.VITE_API_KEY}/instructor-img/${courseData.instructor.instImage}`}
                        alt="강사 이미지"
                        style={{
                          width: '100%',
                          maxWidth: '300px',
                          height: 'auto',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Grid>
                )}

                {/* 강의 교안 파일 */}
                {courseData.lessonPlanStoredName && (
                  <Grid item xs={12}>
                    <InfoLabel>강의 교안 파일 (다운로드 구현 안되어있음)</InfoLabel>
                    <Box mt={1}>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        href={`${import.meta.env.VITE_API_KEY}/course-lesson-plan/${courseData.lessonPlanStoredName}`}
                        target="_blank"
                        download={courseData.lessonPlanOrgName || courseData.lessonPlanStoredName}
                        sx={{ minWidth: 200 }}
                      >
                        {courseData.lessonPlanOrgName || courseData.lessonPlanStoredName}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* 하단: 동영상 목록 */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <SectionHeader sx={{ margin: 0, padding: 0, border: 'none' }}>
                  <PlayIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={600}>
                    동영상 강의 목록
                  </Typography>
                </SectionHeader>
                {courseData.courseCount !== videoList.length && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setVideoAddModalOpen(true)}
                  >
                    동영상 추가
                    
                  </Button>
                )}
              </Stack>

              <DataGrid
                rows={videoList}
                columns={videoColumns}
                loading={videoLoading}
                getRowId={(row) => row.videoId}
                pagination={false}
                height={500}
                noRowsMessage="등록된 동영상이 없습니다."
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 동영상 추가 모달 */}
      <VideoAddModal
        open={videoAddModalOpen}
        onClose={() => setVideoAddModalOpen(false)}
        courseId={courseId}
        courseCategory={courseData.category}
        onSuccess={() => {
          // 동영상 목록 새로고침
          refetchVideoList();
        }}
      />

      {/* 동영상 수정 모달 */}
      <VideoEditModal
        open={videoEditModalOpen}
        onClose={() => {
          setVideoEditModalOpen(false);
          setSelectedVideo(null);
        }}
        video={selectedVideo}
        onSuccess={() => {
          // 동영상 목록 새로고침
          refetchVideoList();
        }}
      />

      {/* 과정 수정 모달 */}
      <CourseEditModal
        open={courseEditModalOpen}
        onClose={() => setCourseEditModalOpen(false)}
        courseData={courseData}
        onSuccess={() => {
          // 과정 정보 새로고침
          refetchCourse();
        }}
      />
    </Container>
  );
};

export default CourseDetailPage;
